// components/canvas/canvas-export.js — EXPORT DROPDOWN
// ═══════════════════════════════════════════════════════════
// Export canvas artifacts as Markdown, HTML file, or plain text.
// PNG/PDF export requires html2canvas/jsPDF which are heavy —
// for now we do client-side text exports and HTML file download.
//
// Usage:
//   <CanvasExportDropdown
//     artifact={artifact}
//     visible={bool}
//     onClose={fn}
//   />
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function stripHtmlToMarkdown(html) {
  // Simple HTML → text conversion for export
  return (html || '')
    .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n## $1\n')
    .replace(/<p[^>]*>(.*?)<\/p>/gi, '\n$1\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
    .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
    .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
    .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
    .replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function CanvasExportDropdown({ artifact, visible, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!visible || !artifact) return null;

  const content = artifact.content || '';
  const title = artifact.title || 'artifact';
  const safeTitle = title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-').toLowerCase() || 'export';
  const kind = artifact.artifact_type || 'text';

  const exports = [];

  // HTML file download — for html artifacts
  if (kind === 'html' || kind === 'htm' || content.includes('<!DOCTYPE') || content.includes('<html')) {
    exports.push({
      label: 'HTML file',
      desc: 'Download as .html',
      icon: '◻',
      action: () => downloadFile(content, `${safeTitle}.html`, 'text/html'),
    });
  }

  // PDF export — uses browser's print-to-PDF via a hidden iframe
  exports.push({
    label: 'PDF',
    desc: 'Print to PDF',
    icon: '⊞',
    action: () => {
      const printContent = kind === 'html' || content.includes('<!DOCTYPE') || content.includes('<html')
        ? content
        : `<!DOCTYPE html><html><head><style>body{font-family:Georgia,serif;color:#333;max-width:700px;margin:40px auto;padding:20px;line-height:1.7;font-size:14px;}h1,h2,h3{margin:20px 0 10px;}pre{background:#f5f5f5;padding:12px;border-radius:6px;overflow-x:auto;font-size:12px;}</style></head><body>${
          content.replace(/^# (.+)$/gm, '<h1>$1</h1>')
            .replace(/^## (.+)$/gm, '<h2>$1</h2>')
            .replace(/^### (.+)$/gm, '<h3>$1</h3>')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\n\n/g, '</p><p>')
            .replace(/^(?!<[hp])/gm, '<p>')
        }</body></html>`;
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.top = '-10000px';
      document.body.appendChild(printFrame);
      printFrame.contentDocument.write(printContent);
      printFrame.contentDocument.close();
      printFrame.onload = () => {
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      };
    },
  });

  // Markdown export
  exports.push({
    label: 'Markdown',
    desc: 'Download as .md',
    icon: '≡',
    action: () => {
      const md = kind === 'html' ? stripHtmlToMarkdown(content) : content;
      downloadFile(md, `${safeTitle}.md`, 'text/markdown');
    },
  });

  // Plain text
  exports.push({
    label: 'Plain text',
    desc: 'Download as .txt',
    icon: '⊡',
    action: () => {
      const text = kind === 'html' ? stripHtmlToMarkdown(content) : content;
      downloadFile(text, `${safeTitle}.txt`, 'text/plain');
    },
  });

  // Copy to clipboard
  exports.push({
    label: copied ? 'Copied!' : 'Copy content',
    desc: 'Copy to clipboard',
    icon: '⎘',
    action: () => {
      try {
        navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (_) { /* clipboard not available */ }
    },
  });

  return (
    <div style={{
      position: 'absolute',
      top: 'calc(100% + 4px)',
      right: 0,
      background: '#07070a',
      border: '1px solid #1e1e2a',
      borderRadius: 8,
      padding: '6px 0',
      minWidth: 180,
      boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
      zIndex: 30,
    }}>
      <div style={{
        padding: '4px 12px 6px',
        fontSize: 9, color: '#333',
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      }}>
        Export
      </div>
      {exports.map(exp => (
        <button
          key={exp.label}
          onClick={() => { exp.action(); if (exp.label !== 'Copy content' && exp.label !== 'Copied!') onClose?.(); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            width: '100%', padding: '7px 12px',
            background: 'none', border: 'none',
            cursor: 'pointer', textAlign: 'left',
            transition: 'background 0.1s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#141414'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
        >
          <span style={{ fontSize: 12, color: '#7a9e82', width: 16, textAlign: 'center' }}>{exp.icon}</span>
          <div>
            <div style={{ fontSize: 11, color: '#d4d4d4', fontFamily: 'Georgia, serif' }}>{exp.label}</div>
            <div style={{ fontSize: 9, color: '#555', fontFamily: 'Georgia, serif' }}>{exp.desc}</div>
          </div>
        </button>
      ))}
    </div>
  );
}
